import type {
  BundleConfig,
  BundleCopy,
  ComponentTemplateSlot,
  OnsiteUpsellConfig,
} from "@intelligems/ig-types/dtos/components";
import type { OnsiteUpsellProduct } from "@intelligems/ig-types/schemas/widgets/onsite-upsell";
import type { OnsiteUpsellProductResolved } from "@intelligems/ig-types/functions/components/resolve-onsite-upsell-products/resolve-onsite-upsell-products";
import type { UpsellDiscount } from "@intelligems/ig-types/dtos/upsell-product-rules";

import {
  shopifyPercentageDiscountPerUnit,
  shouldFlushCompareAtPrice,
} from "./price";

// Onsite-upsell template grammar — shared by the plugin (fills templates) and
// viv (authors them). Slot names live in ig-types (OnsiteUpsellSlot).
export const UPSELL_EACH_OPEN = "{{#each products}}";
export const UPSELL_EACH_CLOSE = "{{/each}}";

export const upsellSlotToken = (slot: ComponentTemplateSlot): string =>
  `{{${slot}}}`;

// Template → HTML runtime, shared by the plugin and the FE preview so the
// rendered markup can't drift. The plugin queries these attrs to hydrate cards;
// the FE preview renders the same markup inert.
export const ATC_MARKER = "ig-onsite-upsell-atc";
export const VARIANT_SELECT_ATTR = "ig-onsite-upsell-variant-select";
export const CARD_ATTR = "ig-onsite-upsell-card";
export const PRICE_ATTR = "ig-onsite-upsell-price";
export const COMPARE_PRICE_ATTR = "ig-onsite-upsell-compare-price";
export const CHECKBOX_ATTR = "ig-bundle-checkbox";
export const BUNDLE_PRICE_ATTR = "ig-bundle-price";
export const BUNDLE_COMPARE_PRICE_ATTR = "ig-bundle-compare-price";
// Dynamic, tier-driven copy the renderer writes on every change.
export const BUNDLE_SUBLINE_ATTR = "ig-bundle-subline";
export const BUNDLE_ROW_BADGE_ATTR = "ig-bundle-row-badge";
export const BUNDLE_SAVED_ATTR = "ig-bundle-saved";

// Canonical designed templates (the Figma "Upsell templates" look), single-
// sourced for the FE default, viv's knowledge, and the panel picker. Storefront
// HTML, so raw hex + a scoped <style> block.
const UPSELL_STYLES = `
    .ig-upsell{--ig-line:color-mix(in srgb,currentColor 20%,transparent);--ig-muted:color-mix(in srgb,currentColor 65%,transparent);box-sizing:border-box;width:100%;min-width:0;max-width:100%;margin-top:16px;padding:16px;border:1px solid var(--ig-line);border-radius:6px;background:transparent;font-family:inherit;color:inherit;text-align:left;}
    .ig-upsell *,.ig-upsell *::before,.ig-upsell *::after{box-sizing:border-box;}
    .ig-upsell__title{margin:0 0 12px;font-size:15px;line-height:22px;font-weight:600;}
    .ig-upsell__items{display:flex;flex-direction:column;gap:8px;}
    .ig-upsell__row{display:flex;align-items:center;gap:12px;}
    .ig-upsell__media{flex:0 0 auto;width:64px;height:64px;border-radius:8px;overflow:hidden;display:block;}
    .ig-upsell__media img{width:100%;height:100%;object-fit:cover;display:block;}
    .ig-upsell__info{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px;}
    .ig-upsell__name{margin:0;font-size:14px;font-weight:600;line-height:1.5;color:inherit;text-decoration:none;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;overflow-wrap:anywhere;}
    .ig-upsell__price{display:flex;gap:6px;align-items:baseline;font-size:12px;line-height:1.5;color:inherit;}
    .ig-upsell [${COMPARE_PRICE_ATTR}]{color:var(--ig-muted);text-decoration:line-through;}
    .ig-upsell [${COMPARE_PRICE_ATTR}]:empty{display:none;}
    .ig-upsell [${VARIANT_SELECT_ATTR}]{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;}
    .ig-upsell [${VARIANT_SELECT_ATTR}]:empty{display:none;}
    .ig-upsell [${VARIANT_SELECT_ATTR}] select{appearance:none;-webkit-appearance:none;width:auto;height:auto;min-height:0;margin:0;box-shadow:none;font-family:inherit;font-size:12px;line-height:1.5;padding:4px 22px 4px 8px;border:1px solid var(--ig-line);border-radius:4px;background-color:transparent;background-image:linear-gradient(45deg,transparent 50%,currentColor 50%),linear-gradient(135deg,currentColor 50%,transparent 50%);background-position:calc(100% - 13px) 55%,calc(100% - 9px) 55%;background-size:4px 4px;background-repeat:no-repeat;color:inherit;max-width:100%;min-width:0;text-overflow:ellipsis;}
    .ig-upsell [${VARIANT_SELECT_ATTR}] select option{background:Canvas;color:CanvasText;}
    .ig-upsell__action{flex:0 0 auto;}
    .ig-upsell [${ATC_MARKER}]{appearance:none;border:0;border-radius:2px;background:#303030;color:#ffffff;font-family:inherit;font-size:14px;font-weight:600;line-height:1.5;padding:8px 12px;cursor:pointer;white-space:nowrap;}
    .ig-upsell [${ATC_MARKER}]:hover{opacity:0.92;}
    .ig-upsell [${ATC_MARKER}]:disabled{opacity:0.5;cursor:not-allowed;}`;

const listTemplate = `<div class="ig-upsell">
  <style>${UPSELL_STYLES}
  </style>
  <div class="ig-upsell__title">You may also like</div>
  <div class="ig-upsell__items">
  ${UPSELL_EACH_OPEN}
    <div class="ig-upsell__row">
      <a class="ig-upsell__media" href="${upsellSlotToken("product.url")}" ig-onsite-upsell-link>
        <img src="${upsellSlotToken("product.image")}" alt="${upsellSlotToken("product.title")}" />
      </a>
      <div class="ig-upsell__info">
        <a class="ig-upsell__name" href="${upsellSlotToken("product.url")}" ig-onsite-upsell-link>${upsellSlotToken("product.title")}</a>
        <div class="ig-upsell__price">${upsellSlotToken("product.price")}${upsellSlotToken("product.compareAtPrice")}</div>
        ${upsellSlotToken("variant-select")}
      </div>
      <div class="ig-upsell__action">${upsellSlotToken("atc")}</div>
    </div>
  ${UPSELL_EACH_CLOSE}
  </div>
</div>`;

const BUNDLE_STYLES = `
    .ig-bundle{--ig-line:color-mix(in srgb,currentColor 20%,transparent);--ig-muted:color-mix(in srgb,currentColor 65%,transparent);--ig-tint:color-mix(in srgb,currentColor 8%,transparent);box-sizing:border-box;width:100%;min-width:0;max-width:100%;margin-top:16px;padding:20px;border:1px solid var(--ig-line);border-radius:6px;background:transparent;font-family:inherit;color:inherit;text-align:left;}
    .ig-bundle *,.ig-bundle *::before,.ig-bundle *::after{box-sizing:border-box;}
    .ig-bundle__title{margin:0;font-size:16px;line-height:24px;font-weight:600;color:inherit;}
    .ig-bundle__subtitle{margin:2px 0 0;font-size:12px;line-height:1.5;font-weight:400;color:var(--ig-muted);}
    .ig-bundle__subtitle:empty{display:none;}
    .ig-bundle__items{margin-top:13px;display:flex;flex-direction:column;gap:12px;padding:12px 0;}
    .ig-bundle__row{display:flex;align-items:center;gap:14px;}
    .ig-bundle__sep{display:flex;align-items:center;justify-content:center;}
    .ig-bundle__sep::before,.ig-bundle__sep::after{content:"";flex:1 1 auto;height:1px;background:var(--ig-line);}
    .ig-bundle__sep span{flex:0 0 auto;display:flex;align-items:center;justify-content:center;width:20px;height:20px;color:var(--ig-muted);}
    .ig-bundle__sep span svg{width:100%;height:100%;display:block;}
    /* Cards are wrapped in display:contents, so the index is the only way to
       reach "the first row" and drop its separator. */
    [${CARD_ATTR}="0"] .ig-bundle__sep{display:none;}
    .ig-bundle__check{flex:0 0 auto;display:flex;align-items:center;}
    .ig-bundle__check input{appearance:none;-webkit-appearance:none;flex:0 0 auto;width:16px;height:16px;margin:0;border:1px solid var(--ig-line);border-radius:4px;background-color:transparent;background-repeat:no-repeat;background-position:center;background-size:11px 11px;box-shadow:none;cursor:pointer;}
    .ig-bundle__check input::before,.ig-bundle__check input::after{content:none;}
    .ig-bundle__check input:checked{background-color:#303030;border-color:#303030;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2.5 6.2L4.9 8.6L9.5 3.4' fill='none' stroke='white' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");}
    .ig-bundle__media{flex:0 0 auto;width:44px;height:44px;border-radius:7px;overflow:hidden;display:block;}
    .ig-bundle__media img{width:100%;height:100%;object-fit:cover;display:block;}
    .ig-bundle__info{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:4px;}
    .ig-bundle__name{margin:0;font-size:12px;font-weight:600;line-height:1.5;color:inherit;text-decoration:none;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;overflow-wrap:anywhere;}
    .ig-bundle [${VARIANT_SELECT_ATTR}]{display:flex;flex-wrap:wrap;gap:6px;}
    .ig-bundle [${VARIANT_SELECT_ATTR}]:empty{display:none;}
    .ig-bundle [${VARIANT_SELECT_ATTR}] select{appearance:none;-webkit-appearance:none;width:auto;height:auto;min-height:0;margin:0;box-shadow:none;font-family:inherit;font-size:12px;line-height:1.5;padding:4px 22px 4px 8px;border:1px solid var(--ig-line);border-radius:4px;background-color:transparent;background-image:linear-gradient(45deg,transparent 50%,currentColor 50%),linear-gradient(135deg,currentColor 50%,transparent 50%);background-position:calc(100% - 13px) 55%,calc(100% - 9px) 55%;background-size:4px 4px;background-repeat:no-repeat;color:inherit;max-width:100%;min-width:0;text-overflow:ellipsis;}
    .ig-bundle [${VARIANT_SELECT_ATTR}] select option{background:Canvas;color:CanvasText;}
    /* Price and its badge, right-aligned in their own column. */
    .ig-bundle__price{flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:4px;}
    .ig-bundle__price-row{display:flex;align-items:baseline;gap:6px;}
    .ig-bundle [${PRICE_ATTR}]{font-size:12px;font-weight:600;line-height:1.5;color:inherit;}
    .ig-bundle [${COMPARE_PRICE_ATTR}]{font-size:12px;font-weight:400;color:var(--ig-muted);text-decoration:line-through;}
    .ig-bundle [${COMPARE_PRICE_ATTR}]:empty{display:none;}
    .ig-bundle [${BUNDLE_ROW_BADGE_ATTR}]{font-size:11px;font-weight:600;line-height:1.5;padding:3px 8px;border-radius:4px;background:var(--ig-tint);color:inherit;white-space:nowrap;}
    .ig-bundle [${BUNDLE_ROW_BADGE_ATTR}]:empty{display:none;}
    .ig-bundle__footer{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px;margin-top:16px;padding-top:16px;border-top:1px solid var(--ig-line);}
    .ig-bundle__totals{display:flex;flex-direction:column;gap:2px;min-width:0;}
    .ig-bundle__totals-label-row{display:flex;flex-wrap:wrap;align-items:center;gap:6px;}
    .ig-bundle__totals-label{font-size:12px;line-height:1.5;color:var(--ig-muted);}
    .ig-bundle [${BUNDLE_SAVED_ATTR}]{font-size:11px;font-weight:600;line-height:1.5;padding:3px 8px;border-radius:4px;background:#303030;color:#ffffff;white-space:nowrap;}
    .ig-bundle [${BUNDLE_SAVED_ATTR}]:empty{display:none;}
    .ig-bundle__totals-amounts{display:flex;gap:8px;align-items:baseline;}
    .ig-bundle [${BUNDLE_PRICE_ATTR}]{font-size:18px;font-weight:600;line-height:26px;color:inherit;}
    .ig-bundle [${BUNDLE_COMPARE_PRICE_ATTR}]{font-size:13px;color:var(--ig-muted);text-decoration:line-through;}
    .ig-bundle [${BUNDLE_COMPARE_PRICE_ATTR}]:empty{display:none;}
    .ig-bundle [${ATC_MARKER}]{appearance:none;border:0;border-radius:4px;background:#303030;color:#ffffff;font-family:inherit;font-size:14px;font-weight:600;line-height:1.5;padding:8px;margin-left:auto;max-width:100%;cursor:pointer;white-space:normal;}
    .ig-bundle [${ATC_MARKER}]:hover{opacity:0.92;}
    .ig-bundle [${ATC_MARKER}]:disabled{opacity:0.5;cursor:not-allowed;}`;

// The plus is a hole in the disc, so it shows the page background and contrasts on any theme.
const bundlePlusSvg = `<svg viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M0 10a10 10 0 1 0 20 0a10 10 0 1 0-20 0ZM9.4 7h1.2v2.4H13v1.2h-2.4V13H9.4v-2.4H7V9.4h2.4Z"/></svg>`;

const bundleTemplate = `<div class="ig-bundle">
  <style>${BUNDLE_STYLES}
  </style>
  <div class="ig-bundle__title">${upsellSlotToken("heading")}</div>
  <div class="ig-bundle__subtitle" ${BUNDLE_SUBLINE_ATTR}>${upsellSlotToken("subheading")}</div>
  <div class="ig-bundle__items">
  ${UPSELL_EACH_OPEN}
    <div class="ig-bundle__sep" aria-hidden="true"><span>${bundlePlusSvg}</span></div>
    <div class="ig-bundle__row">
      <div class="ig-bundle__check">${upsellSlotToken("checkbox")}</div>
      <a class="ig-bundle__media" href="${upsellSlotToken("product.url")}" ig-onsite-upsell-link><img src="${upsellSlotToken("product.image")}" alt="${upsellSlotToken("product.title")}" /></a>
      <div class="ig-bundle__info">
        <a class="ig-bundle__name" href="${upsellSlotToken("product.url")}" ig-onsite-upsell-link>${upsellSlotToken("product.title")}</a>
        ${upsellSlotToken("variant-select")}
      </div>
      <div class="ig-bundle__price">
        <div class="ig-bundle__price-row">${upsellSlotToken("product.price")}${upsellSlotToken("product.compareAtPrice")}</div>
        <span class="ig-bundle__row-badge" ${BUNDLE_ROW_BADGE_ATTR}></span>
      </div>
    </div>
  ${UPSELL_EACH_CLOSE}
  </div>
  <div class="ig-bundle__footer">
    <div class="ig-bundle__totals">
      <div class="ig-bundle__totals-label-row">
        <span class="ig-bundle__totals-label">Bundle price</span>
        <span class="ig-bundle__saved" ${BUNDLE_SAVED_ATTR}></span>
      </div>
      <div class="ig-bundle__totals-amounts">${upsellSlotToken("bundle.price")}${upsellSlotToken("bundle.comparePrice")}</div>
    </div>
    ${upsellSlotToken("bundle.atc")}
  </div>
</div>`;

export const ONSITE_UPSELL_TEMPLATES = {
  list: listTemplate,
  bundle: bundleTemplate,
} as const;

export type OnsiteUpsellTemplateLayout = keyof typeof ONSITE_UPSELL_TEMPLATES;

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Substitutes the {{slot}} tokens in one product's template block.
export function fillItem(
  itemTemplate: string,
  product: OnsiteUpsellProduct
): string {
  return (
    itemTemplate
      .replaceAll(upsellSlotToken("product.image"), escapeHtml(product.image))
      .replaceAll(upsellSlotToken("product.url"), escapeHtml(product.url))
      .replaceAll(upsellSlotToken("product.title"), escapeHtml(product.title))
      .replaceAll(
        upsellSlotToken("product.price"),
        `<span ${PRICE_ATTR}>${escapeHtml(product.price)}</span>`
      )
      .replaceAll(
        upsellSlotToken("product.compareAtPrice"),
        `<span ${COMPARE_PRICE_ATTR}>${escapeHtml(product.compareAtPrice)}</span>`
      )
      .replaceAll(
        upsellSlotToken("variant-select"),
        `<div ${VARIANT_SELECT_ATTR}></div>`
      )
      .replaceAll(
        upsellSlotToken("atc"),
        `<button ${ATC_MARKER} type="button">Add to cart</button>`
      )
      // Renders checked; the renderer removes these when selection is off.
      .replaceAll(
        upsellSlotToken("checkbox"),
        `<input ${CHECKBOX_ATTR} type="checkbox" checked />`
      )
  );
}

// Price nodes come out empty: the renderer writes the total on every selection
// change.
export function fillBundleShell(shell: string, copy: BundleCopy): string {
  return shell
    .replaceAll(upsellSlotToken("heading"), escapeHtml(copy.heading))
    .replaceAll(
      upsellSlotToken("subheading"),
      escapeHtml(copy.subheading ?? "")
    )
    .replaceAll(
      upsellSlotToken("bundle.price"),
      `<span ${BUNDLE_PRICE_ATTR}></span>`
    )
    .replaceAll(
      upsellSlotToken("bundle.comparePrice"),
      `<span ${BUNDLE_COMPARE_PRICE_ATTR}></span>`
    )
    .replaceAll(
      upsellSlotToken("bundle.atc"),
      `<button ${ATC_MARKER} type="button">${escapeHtml(copy.buttonText)}</button>`
    );
}

// Splits a template into the fixed shell and the {{#each products}}…{{/each}}
// block that repeats per product. No markers → the whole template is one item.
export function parseTemplate(template: string): {
  before: string;
  itemTemplate: string;
  after: string;
} {
  const blockStart = template.indexOf(UPSELL_EACH_OPEN);
  const blockEnd = template.indexOf(UPSELL_EACH_CLOSE);

  const hasItemBlock =
    blockStart !== -1 && blockEnd !== -1 && blockStart < blockEnd;
  if (!hasItemBlock) {
    return { before: "", itemTemplate: template, after: "" };
  }

  return {
    before: template.slice(0, blockStart),
    itemTemplate: template.slice(
      blockStart + UPSELL_EACH_OPEN.length,
      blockEnd
    ),
    after: template.slice(blockEnd + UPSELL_EACH_CLOSE.length),
  };
}

// Fills one product's template, wrapped in a display:contents container tagged
// with its index (links the rendered card back to its product for hydration).
export function productCardHtml(
  itemTemplate: string,
  product: OnsiteUpsellProduct,
  cardIndex: number
): string {
  return `<div ${CARD_ATTR}="${cardIndex}" style="display:contents">${fillItem(
    itemTemplate,
    product
  )}</div>`;
}

// The fixed template shell around one card per product.
export function renderUpsellTemplate(
  template: string,
  products: OnsiteUpsellProduct[]
): string {
  const { before, itemTemplate, after } = parseTemplate(template);
  return (
    before +
    products
      .map((product, index) => productCardHtml(itemTemplate, product, index))
      .join("") +
    after
  );
}

// `cards[i]` is the rendered card for `products[i]`.
export function mountUpsell(
  container: Element,
  template: string,
  products: OnsiteUpsellProduct[]
): (Element | null)[] {
  container.innerHTML = renderUpsellTemplate(template, products);
  return products.map((_, index) =>
    container.querySelector(`[${CARD_ATTR}="${index}"]`)
  );
}

// `{{product.image}}` only fills attributes, so there is no marker to query:
// the card's product images are the <img>s rendered with the product image.
export function syncVariantImage(
  card: Element,
  product: OnsiteUpsellProduct
): (variantImage: string | undefined) => void {
  const images = Array.from(card.querySelectorAll("img")).filter(
    (img) => img.getAttribute("src") === product.image
  );
  return (variantImage) => {
    images.forEach((img) => {
      img.src = variantImage || product.image;
    });
  };
}

// The bundle shell around one card per product; prices are written later by
// `writeBundlePricing`.
export function renderBundleTemplate(
  config: Pick<BundleConfig, "template" | "copy">,
  products: OnsiteUpsellProduct[]
): string {
  const { before, itemTemplate, after } = parseTemplate(config.template);
  return (
    fillBundleShell(before, config.copy) +
    products
      .map((product, index) => productCardHtml(itemTemplate, product, index))
      .join("") +
    fillBundleShell(after, config.copy)
  );
}

// The endpoint returns raw prices; the card shows the discounted price. Shared
// by the plugin and the FE preview (only the money formatter is injected).
// Percentage discounts floor to the cent like Shopify.
export function applyUpsellDiscount(
  price: number,
  discount: UpsellDiscount | undefined
): number {
  if (
    !discount ||
    discount.discountType === "none" ||
    discount.discountValue == null
  ) {
    return price;
  }
  if (discount.discountType === "percentage") {
    return (
      price - shopifyPercentageDiscountPerUnit(price, discount.discountValue)
    );
  }
  return Math.max(0, price - discount.discountValue);
}

// The struck-through reference price; `compareAtPriceDisplay` picks which one.
export function upsellCompareAtPrice(
  regularPrice: number,
  shopifyCompareAt: number | undefined,
  discount: UpsellDiscount | undefined
): number | undefined {
  if (!discount || discount.discountType === "none") {
    return shopifyCompareAt;
  }
  switch (discount.compareAtPriceDisplay) {
    case "regularPrice":
      return regularPrice;
    case "compareAtPrice":
      return shopifyCompareAt;
    default:
      return undefined;
  }
}

// Maps a resolved product (numeric prices) to a render product (formatted,
// discount-applied strings).
export function toOnsiteUpsellRenderProduct(
  product: OnsiteUpsellProductResolved,
  discount: UpsellDiscount | undefined,
  formatMoney: (amount: number, currencyCode: string) => string
): OnsiteUpsellProduct {
  const renderPrice = (price: number, compareAt: number | undefined) => {
    const discounted = applyUpsellDiscount(price, discount);
    const compare = upsellCompareAtPrice(price, compareAt, discount);
    return {
      price: formatMoney(discounted, product.currencyCode),
      compareAtPrice:
        compare != null && !shouldFlushCompareAtPrice(discounted, compare)
          ? formatMoney(compare, product.currencyCode)
          : "",
    };
  };

  return {
    productId: product.productId,
    url: product.url,
    title: product.title,
    image: product.image,
    ...renderPrice(product.price, product.compareAtPrice ?? undefined),
    options: product.options,
    ...(product.defaultVariantId === undefined
      ? {}
      : { defaultVariantId: product.defaultVariantId }),
    variants: product.variants.map((variant) => ({
      id: variant.id,
      availableForSale: variant.availableForSale,
      image: variant.image,
      ...renderPrice(variant.price, variant.compareAtPrice ?? undefined),
      options: variant.options,
      sig: variant.sig,
    })),
  };
}

/**
 * True when an onsite upsell is served by the Agentic Logic engine rather than
 * by its legacy `rules`.
 *
 * A saved binding (`agenticLogicPlacementId`) or a rule the author picked in
 * the builder but has not saved yet (`agenticLogicRuleId`) both mean the
 * engine owns it. Either way `rules` is only the legacy rollback config and
 * must not be read — it is never empty, because the schema requires it.
 */
export function isAgenticLogicOnsiteUpsell(
  config: Pick<
    OnsiteUpsellConfig,
    "agenticLogicPlacementId" | "agenticLogicRuleId"
  >
): boolean {
  return !!config.agenticLogicPlacementId || !!config.agenticLogicRuleId;
}

/**
 * The products an onsite upsell renders, from what its resolve returned.
 *
 * A component served by the Agentic Logic engine renders what the server
 * returned: the rule's `settings.maxItems` owns the count and the server
 * already trimmed to it, so the container's own `maxProductLimit` — kept only
 * for the legacy engine — must not cap it a second time. A legacy component
 * resolves `maxProductLimit` plus a rotation reserve, so it slices here.
 */
export function onsiteUpsellProductsToRender<T>(
  config: Pick<
    OnsiteUpsellConfig,
    "maxProductLimit" | "agenticLogicPlacementId" | "agenticLogicRuleId"
  >,
  products: readonly T[]
): T[] {
  return isAgenticLogicOnsiteUpsell(config)
    ? [...products]
    : products.slice(0, config.maxProductLimit);
}
